import {
  Collapse,
  Divider,
  Group,
  List,
  Stack,
  Text,
  Timeline,
} from "@mantine/core";
import React, { useState } from "react";
import classes from "./CardTimeline.module.css";
import PlusMinusButton from "src/components/PlusMinusButton";
import { useMediaQuery } from "@mantine/hooks";

export type CardTimelineItem = {
  iconClassName: string;
  title: string;
  employer: string;
  start?: string;
  end: string;
  content: {
    description?: string;
    sections: {
      title: string;
      description?: string;
      content?: {
        title?: string;
        description: string;
      }[];
    }[];
  };
  skills?: {
    category?: string;
    skills: string[];
  }[];
};

type Props = {
  alwaysOpen?: boolean;
  itemList: CardTimelineItem[];
};

export const CardTimeline = ({ alwaysOpen, itemList }: Props) => {
  const [target, setTarget] = useState<number | undefined>();

  const mobile = useMediaQuery("(max-width: 46em");

  const toggle = (item: number) => {
    if (target === item) {
      setTarget(undefined);
    } else {
      setTarget(item);
    }
  };

  const renderCollapsableContent = (item: CardTimelineItem, index: number) => {
    return (
      <Collapse
        in={alwaysOpen || target === index}
        className={classes["collapse-section"]}
      >
        <div>
          {item.content.description &&
            item.content.description
              .split("\n")
              .map((line, index) => (
                <div key={`description_${index}`}>{line}</div>
              ))}
          {item.content.sections.map((section, s_index) => (
            <div key={`${section.title}_${s_index}`}>
              <Text className={classes["section-header"]}>{section.title}</Text>
              {section.description && (
                <Text className={classes["section-description"]}>
                  {section.description}
                </Text>
              )}
              {section.content && section.content?.length && (
                <List
                  className={classes["section-list"]}
                  spacing="xs"
                  size="xs"
                  withPadding
                >
                  {section.content?.map((subSection, c_index) => (
                    <List.Item
                      key={`${subSection.description}_${s_index}_${c_index}`}
                    >
                      {subSection.title && (
                        <Text className={classes["sub-section-title"]}>
                          {`${subSection.title}: `}
                        </Text>
                      )}
                      <Text className={classes["sub-section-content"]}>
                        {subSection.description}
                      </Text>
                    </List.Item>
                  ))}
                </List>
              )}
            </div>
          ))}
          {item.skills && (
            <React.Fragment>
              <Divider />
              <div>
                {item.skills.map((skillCategory, index) => (
                  <div
                    key={`${skillCategory.category}_${index}`}
                    className={classes["skill-category"]}
                  >
                    {skillCategory.category && (
                      <Text className={classes["skill-category-title"]}>
                        {skillCategory.category}:
                      </Text>
                    )}
                    <Text className={classes["skill-category-content"]}>
                      {skillCategory.skills.join(", ")}
                    </Text>
                  </div>
                ))}
              </div>
            </React.Fragment>
          )}
        </div>
      </Collapse>
    );
  };

  const renderHeaderContent = (item: CardTimelineItem, index: number) => {
    return (
      <Group
        align="flex-start"
        gap={0}
        justify="space-between"
        className={classes["header-group"]}
      >
        <Stack gap={"xs"}>
          <Text className={classes["role-title"]}>{item.title}</Text>
          <Text
            className={classes.subtext}
            size="sm"
          >{`${item.employer}`}</Text>
          <Text className={classes.subtext} size="sm">
            {item.start ? `${item.start} - ${item.end}` : `${item.end}`}
          </Text>
        </Stack>
        {!alwaysOpen && (
          <div className={classes["plus-minus-button-container"]}>
            <PlusMinusButton active={target === index} />
          </div>
        )}
      </Group>
    );
  };

  const renderTimelineItem = (item: CardTimelineItem, index: number) => {
    return (
      <Timeline.Item
        key={`${item.title}_${index}`}
        title={
          <div onClick={() => toggle(index)}>
            {renderHeaderContent(item, index)}
          </div>
        }
        bullet={
          <div className={item.iconClassName}>
            <div />
          </div>
        }
      >
        {renderCollapsableContent(item, index)}
      </Timeline.Item>
    );
  };

  return mobile ? (
    <Stack>
      {itemList.map((item, index) => {
        return (
          <div
            key={`${item.title}_container_${index}`}
            className={classes["mobile-item"]}
          >
            <div onClick={() => toggle(index)}>
              {renderHeaderContent(item, index)}
            </div>
            {renderCollapsableContent(item, index)}
          </div>
        );
      })}
    </Stack>
  ) : (
    <Timeline className={classes.timeline} bulletSize={"3em"} lineWidth={4}>
      {itemList.map(renderTimelineItem)}
    </Timeline>
  );
};
